import CsiFormData from "csi/action/FormData";
import { EMPTY_URL } from "csi/constants";
import NodeDataStore from "csi/datastore/NodeDataStore";
import { FormWidget } from "csi/form/Widget";
import locale from "csi/locale";
import { getRuntimeRulesQuery } from "csi/middleware/attributeRule/RuleService";
import { ATTR_MEDIUM_IMAGE, DEFAULT_IMAGE_PATH, getImageSrc, SMALL_IMAGE_SIZE } from "csi/model/image";
import { waitForPDFRequestSucceed } from "csi/model/pdfRequest";
import { getTypeAttributeDestination } from "csi/reflection/reflection";
import { getSearch } from "csi/service/dataStoreService";
import { requestUrl, resolveNodesWithDependencies } from "csi/service/nodeService";
import { submitWriter, writeFormFields } from "csi/service/writerService";
import rootStore from "csi/state/rootStore";
import { DEFAULT_FORM_ID } from "csi/state/viewUtils";
import { C8Node, cnl, ICache } from "csi/typings";
import { arrayMove } from "csi/utility/arrayUtility";
import { pathCreate } from "csi/utility/pathUtility";
import csiView from "csi/view";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import Writer from "csi/xml/Writer";
import { MasonryGridImage } from "designSystem/components/masonrygrid/MasonryGrid";
import { ComponentItemDataStore } from "designSystem/utilities/component-item-functions";
import _ from "lodash";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { createContext, useContext } from "react";
import {
	ColorComponentItem,
	defaultColorUsedInPresentation,
	getPresentationColorMatrixPath,
	getPresentationRuntimeRules,
	getProductColorSpecImage,
	onApplyPresentationProductFilter,
	PDF_POLLING_INTERVAL,
	PDF_TIMEOUT,
	PRESENTATION_SETS,
	PRODUCT_PRESENTATION_SET,
	SET_ITEM_COLOR,
	SET_ITEM_PRODUCT,
	SET_ITEMS
} from "../PPService";
import PresentationPageStore from "../PresentationPageStore";

interface ItemValue {
	colorUrl: cnl;
	itemUrl: cnl;
	productUrl: cnl;
}

export class PPSetItem {
	private _itemUrl: cnl;
	@observable private _hasViolation: boolean;

	/** data store of product colors */
	readonly colorStore: ComponentItemDataStore<ColorComponentItem>;

	/** color url */
	@observable colorUrl: cnl;

	/** item url, if existing set item node */
	get itemUrl(): cnl {
		return this._itemUrl;
	}

	/** product url */
	readonly productUrl: cnl;

	/** indicate if this item has violation  */
	get hasViolation(): boolean {
		return this._hasViolation;
	}

	/** return value for item */
	@computed get value(): ItemValue {
		const { colorUrl, itemUrl, productUrl } = this;
		return { colorUrl, itemUrl, productUrl };
	}

	/** set selected color */
	@action setColor = (value: cnl): void => {
		this.colorUrl = value;
	};

	/** set violation */
	@action setViolation = (hasViolation: boolean): void => {
		this._hasViolation = hasViolation;
	};

	constructor(productUrl: cnl, colorUrl?: cnl, colorStore?: ComponentItemDataStore<ColorComponentItem>, itemUrl?: cnl, hasViolation?: boolean) {
		makeObservable(this);
		this.colorStore = colorStore;
		this.colorUrl = colorUrl || EMPTY_URL;
		this._itemUrl = itemUrl || EMPTY_URL;
		this.productUrl = productUrl;
		this._hasViolation = hasViolation;
	}
}

class SetEditStore {
	@observable.ref private _colorRuleQuery: Query;
	private readonly _items = observable.array<PPSetItem>([], { deep: false });
	@observable.ref private _formData: CsiFormData;
	@observable private _formFieldChange = false;
	@observable private _leftToRight = false;
	@observable.ref private _productRuleQuery: Query;	// TODO: will be used for validation to flag invalid products
	@observable private _setUrl: cnl;

	// the start value of orientation, internal use, to determine if set changes
	private _startOrientation: boolean;

	private _startValue: ItemValue[];

	/** cache */
	readonly cache: ICache;

	/** presentation url */
	readonly presentationUrl: cnl;

	/** form data */
	get formData(): CsiFormData {
		return this._formData;
	}

	/** indicates if user has made a change to the set value */
	get hasChanged(): boolean {
		const { value, _formFieldChange, _startValue, _startOrientation, leftToRight } = this;
		return _formFieldChange || !_.isEqual(value, _startValue) || _startOrientation !== leftToRight;
	}

	/** indicates if there are items in the set */
	get hasItems(): boolean {
		return !!this._items.length;
	}

	/** return objects to populate masonry grid */
	@computed get imageList(): MasonryGridImage[] {
		const { getItemImage, _items } = this;

		return _items.map((item) => {
			const image = getItemImage(item);

			return {
				link: getImageSrc(image, null, ATTR_MEDIUM_IMAGE),
				height: image?.Height || SMALL_IMAGE_SIZE,
				width: image?.Width || SMALL_IMAGE_SIZE,
			};
		});
	}

	/** item list */
	get items(): PPSetItem[] {
		return this._items;
	}

	/** orientation of masonry grid */
	get leftToRight(): boolean {
		return this._leftToRight;
	}

	/** number of items in the set */
	get numberOfItems(): number {
		return this._items.length;
	}

	/** presentation set node */
	get ppSet(): C8Node {
		return this.cache.get(this.setUrl);
	}

	/** page store */
	get presentationPageStore(): PresentationPageStore {
		return rootStore.pageStore as PresentationPageStore;
	}

	/** rules for products in presentation */
	get productRuleQuery(): Query {
		return this._productRuleQuery;
	}

	/** set url */
	get setUrl(): cnl {
		return this._setUrl;
	}

	/** return value of form */
	@computed get value(): ItemValue[] {
		return this._items.map((item) => item.value);
	}

	/** add item to set */
	addItem = async (productUrl: cnl, colorUrl?: cnl): Promise<void> => {
		const { cache, _createSetItem, _items } = this;
		await resolveNodesWithDependencies([productUrl, colorUrl], [DEFAULT_IMAGE_PATH], cache);
		const item = _createSetItem(productUrl, colorUrl);

		runInAction(() => {
			_items.push(item);
		});
	};

	/** copy item in set */
	@action copyItem = (index: number): void => {
		const { _createSetItem, _items } = this;
		const { colorUrl, productUrl } = _items[index];
		_items.splice(index + 1, 0, _createSetItem(productUrl, colorUrl));
	};

	/** delete item from set */
	@action deleteItem = (index: number): void => {
		const { _items } = this;
		const item = _items[index];
		_items.remove(item);
	};

	/** indicate the a form field has changed value */
	@action formFieldChanged = (): void => {
		this._formFieldChange = true;
	};

	/** drop item from one index to another */
	@action onDropItem = async (dragIndex: number, dropIndex: number): Promise<boolean> => {
		arrayMove(this._items, dragIndex, dropIndex);
		return true;
	};

	/** return item at given index */
	getItemByIndex = (index: number): PPSetItem => {
		return this._items[index];
	};

	/** return item at given index */
	getItemImage = (item: PPSetItem): C8Node | null => {
		const { cache } = this;
		const { colorUrl, productUrl } = item;
		const color = cache.get(colorUrl);

		// if color not in cache, then cannot get its image
		if (xml.isValidURL(colorUrl) && !color) {
			return null;
		}

		return cache.getPathNode(color || productUrl, DEFAULT_IMAGE_PATH);
	};

	/** save changes to set; if return value is false, then masonry grid image was not generated (yet) */
	saveChanges = async (): Promise<boolean> => {
		const { cache, formData, presentationUrl, setUrl, value, leftToRight } = this;
		const writer = new Writer();
		const ppSetItemUrls: cnl[] = [];
		const ppSetItemProductUrls: cnl[] = [];
		const ppSetItemProductColorUrls: cnl[] = [];
		let url = setUrl;

		value.forEach(({ colorUrl, itemUrl, productUrl }) => {
			ppSetItemUrls.push(itemUrl);
			ppSetItemProductUrls.push(productUrl);
			ppSetItemProductColorUrls.push(colorUrl);
		});

		if (!url) {
			url = await requestUrl();
			writer.createNode(url, PRODUCT_PRESENTATION_SET).attach(presentationUrl, PRESENTATION_SETS);
		}

		writeFormFields(writer, url, formData.getFields(), !!setUrl);

		writer.callMethod("ProductPresentation", "EditPresentationSet")
			.addParameter("PPSetUrl", xml.ATTR_CNL, url)
			.addParameter("LeftToRight", xml.ATTR_BOOLEAN, leftToRight)
			.addParameter("PPSetItemUrls", xml.ATTR_REFVECTOR, ppSetItemUrls)
			.addParameter("PPSetItemProductUrls", xml.ATTR_REFVECTOR, ppSetItemProductUrls)
			.addParameter("PPSetItemProductColorUrls", xml.ATTR_REFVECTOR, ppSetItemProductColorUrls);

		const response = await submitWriter(writer);
		const pdfRequest = response?.getNodeOfType("PDFRequest");

		runInAction(() => {
			this._setUrl = url;
			cache.cacheResponse(response);
		});

		try {
			await waitForPDFRequestSucceed(pdfRequest, PDF_POLLING_INTERVAL, PDF_TIMEOUT);

			// refresh Sets tab if view exists
			csiView.getView("Sets")?.refreshData();
		} catch (err) {
			return false;
		}

		return true;
	};

	/** set orientation of the masonry grid */
	setItemColor = async (item: PPSetItem, value: cnl): Promise<void> => {
		const { getViolationOfProduct, getViolationOfColor } = this.presentationPageStore;
		const url = (value === EMPTY_URL) ? item.productUrl : value;

		const productHasViolation = getViolationOfProduct(item.productUrl);
		const colorHasViolation = getViolationOfColor(value);
		const hasViolation = productHasViolation || colorHasViolation;

		await resolveNodesWithDependencies([url], [DEFAULT_IMAGE_PATH], this.cache);

		runInAction(() => {
			item.setColor(value);
			item.setViolation(hasViolation);
		});
	};

	/** validate changes and return true if valid */
	validateChanges = (): boolean => {
		const formWidget = csiView.getView(DEFAULT_FORM_ID) as FormWidget;
		const errorParams = {};
		return formWidget.validateForm(errorParams);
	};

	/** set orientation of the masonry grid */
	@action setLeftToRight = (value: boolean): void => {
		this._leftToRight = value;
	};

	/** create data store for item colors */
	private _createSetItem = (productUrl: cnl, colorUrl: cnl, itemUrl?: cnl): PPSetItem => {
		const { cache, _colorRuleQuery, presentationUrl, presentationPageStore } = this;
		const { getViolationOfProduct, getViolationOfColor } = presentationPageStore;

		const productHasViolation = getViolationOfProduct(productUrl);
		const colorHasViolation = getViolationOfColor(colorUrl);

		const product = cache.get(productUrl);
		const presentation = cache.get(presentationUrl);
		const type = product.$Type;
		const colorType = getTypeAttributeDestination(type, "ProductColors")?.type;
		const allColorNodeUrls = product.ProductColors;

		// get color spec
		const _getColorSpec = (color: C8Node, hasViolation: boolean): ColorComponentItem => {
			const href = getProductColorSpecImage(color, cache);
			const name = locale.getNodeConfiguredName(color, cache);

			return {
				disabled: hasViolation,
				hasViolation,
				href,
				id: color.$URL,
				name,
			};
		};

		// get default color spec
		const _getDefaultColorSpec = (allColorsHasViolation: boolean): ColorComponentItem | null => {
			const defaultSpec = {
				id: productUrl,
				name: "",
				res: "Default",
			};
			const defaultViolationSpec = {
				...defaultSpec,
				hasViolation: true,
				disabled: true,
			};

			// in the case of violation
			if (allColorsHasViolation || productHasViolation) {
				// check if default one is used in Set
				const defaultIsUsedInSetUnderTheCurrentPP = defaultColorUsedInPresentation(cache, presentation, product);

				if (defaultIsUsedInSetUnderTheCurrentPP) {
					return defaultViolationSpec;
				}

				return null;
			}

			return defaultSpec;
		};

		const colorStore = new ComponentItemDataStore(null, async () => {
			const search = getSearch(`${colorType}.${type}`, productUrl);
			search.addDependencyPaths([pathCreate("ColorSpecification")]);
			search.getQuery().importQuery(_colorRuleQuery);
			search.getSortQuery().SortByNode(xml.PROP_NAME);

			const store = new NodeDataStore(search, cache);
			const total = await store.load();

			const stillValidColors: C8Node[] = [];
			const colors: ColorComponentItem[] = [];

			for (let i = 0; i < total; ++i) {
				stillValidColors.push(store.get(i));
			}

			const invalidColorUrls = _.difference(allColorNodeUrls, stillValidColors.map((color) => color.$URL));
			const invalidColorNodes = cache.getNodes(invalidColorUrls);
			const violationColors = invalidColorNodes.filter((node) => node.Presentations.includes(presentationUrl));

			const allColorsHasViolation = (violationColors.length === allColorNodeUrls?.length);
			const defaultColor = _getDefaultColorSpec(allColorsHasViolation);

			if (defaultColor) {
				colors.push(defaultColor);
			}

			// step 1. include the normal colors
			for (const color of stillValidColors) {
				if (!productHasViolation || color.Presentations.includes(presentationUrl)) {
					colors.push(_getColorSpec(color, productHasViolation));
				}
			}

			// step2. include the colors with violations which used in sets
			for (const color of violationColors) {
				colors.push(_getColorSpec(color, true));
			}

			_.sortBy(colors, ["name"]);

			return colors;
		});

		const hasViolation = productHasViolation || colorHasViolation;

		return new PPSetItem(productUrl, colorUrl, colorStore, itemUrl, hasViolation);
	};

	/** initialize the store */
	private _init = async (): Promise<void> => {
		const { cache, presentationUrl, setUrl } = this;
		const runtimeRules = await getPresentationRuntimeRules(presentationUrl, cache);

		runInAction(() => {
			// initialize rule queries
			const presentation = cache.get(presentationUrl);
			const colorMatrixPath = getPresentationColorMatrixPath(presentation);

			this._productRuleQuery = getRuntimeRulesQuery(runtimeRules, null, onApplyPresentationProductFilter);
			this._colorRuleQuery = getRuntimeRulesQuery(runtimeRules, colorMatrixPath);

			// initialize items
			const ppset = cache.get(setUrl);
			if (ppset) {
				const nodes = cache.getNodes(ppset[SET_ITEMS]);

				// collect invalid color urls
				const colorUrls: Set<cnl> = new Set();

				nodes.forEach((item) => {
					const { PPIProductColor } = item;

					if (xml.isValidURL(PPIProductColor)) {
						colorUrls.add(PPIProductColor);
					}
				});

				this._leftToRight = ppset.LeftToRight;

				for (const node of nodes) {
					const productUrl = node[SET_ITEM_PRODUCT];

					// either product or individual color has violation, show the indication
					const colorUrl = node[SET_ITEM_COLOR] === EMPTY_URL ? productUrl : node[SET_ITEM_COLOR];
					this._items.push(this._createSetItem(productUrl, colorUrl, node.$URL));
				}
			}

			// store starting value of set after initializing items
			this._startValue = this.value;
			this._startOrientation = this._leftToRight;

			// create form data necessary to support Dojo view
			this._formData = new CsiFormData({
				getAction: (): null => null,
				getCache: (): ICache => cache,
				getNode: (): C8Node => ppset,
				getParent: (): null => null,
				getContext: (): null => null,
			});
		});
	};

	constructor(presentationUrl: cnl, setUrl: cnl, cache: ICache) {
		makeObservable(this);
		this.cache = cache;
		this.presentationUrl = presentationUrl;
		this._setUrl = setUrl;
		this._init();
	}
}

export default SetEditStore;

export const SetEditStoreContext = createContext<SetEditStore>(undefined);
export const useSetEditStore = (): SetEditStore => useContext(SetEditStoreContext);